import { Device } from "@capacitor/device";

import { LANGUAGE_STORAGE_KEY } from "../i18n.config";
import { resolveSupportedLocale } from "../resolveLocale/resolveLocale";

/**
 * On first launch (before any language has been persisted), seed the stored
 * language from the device/OS language so Capacitor apps open in the user's
 * language automatically. Once the user picks a language via the switcher, that
 * choice is persisted and this becomes a no-op.
 *
 * Must run after `initStorage()` (so the cache is warm) and before `initI18n()`
 * (so the detector picks up the seeded value).
 */
export async function applyDeviceLanguage(): Promise<void> {
  try {
    // Imported lazily so the i18n barrel never statically pulls the storage
    // singleton into the module graph (keeps it out of unit tests).
    const { storage } = await import("@/modules/storage");

    if (storage.getItem(LANGUAGE_STORAGE_KEY)) {
      return; // A language is already stored (user choice or prior detection).
    }

    const { value } = await Device.getLanguageTag();
    const resolved = resolveSupportedLocale(value);

    if (resolved) {
      storage.setItem(LANGUAGE_STORAGE_KEY, resolved);
    }
  } catch (error) {
    // Device locale is a best-effort enhancement; never block startup on it.
    console.warn("[i18n] Unable to resolve device language", error);
  }
}
